import { Accordion } from '@positivote/design-system/components/Accordion'
import { Button } from '@positivote/design-system/components/Button'
import { Div } from '@positivote/design-system/components/Div'
import { Divider } from '@positivote/design-system/components/Divider'
import { Typography } from '@positivote/design-system/components/Typography'
import { AlertErrorIcon } from '@positivote/design-system/icons/AlertError'
import { CheckCircleIcon } from '@positivote/design-system/icons/CheckCircle'
import { PlayCircleIcon } from '@positivote/design-system/icons/PlayCircle'
import { useEffect, useState } from 'react'

import { i18n } from '@/common/i18n'

export interface AccordionVideoProps {
  urlVideo: string | undefined
  onChangeVideoResult: (result: string) => void
}

export function AccordionVideo({
  urlVideo,
  onChangeVideoResult
}: AccordionVideoProps): JSX.Element {
  const [playerVideoPros, setPlayerVideoProps] = useState({
    isVisible: false,
    testResult: ''
  })

  useEffect(() => {
    onChangeVideoResult(playerVideoPros.testResult)
    // DOCS: This useeffect can only be used when changing states
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerVideoPros.testResult])

  return (
    <Accordion
      isOpen={true}
      Summary={
        <Typography variant="titleMedium">
          {i18n().modules.requirements.components.accordionVideo.title}
        </Typography>
      }
      css={{
        borderRadius: '$lg',
        backgroundColor: '$surface-2',
        overflow: 'hidden',
        '& .Accordion-Details': {
          backgroundColor: '$background'
        }
      }}
    >
      <Divider />

      <Div
        css={{
          display: 'flex',
          gap: '$md',
          justifyContent: 'space-between',
          padding: '$md $lg $md $3xl',
          alignItems: 'center',
          position: 'relative',
          '@xs': {
            flexDirection: 'column',
            alignItems: 'start'
          }
        }}
      >
        {!playerVideoPros.isVisible && !playerVideoPros.testResult.length && (
          <>
            <Typography variant="bodyMedium" css={{ color: '$on-surface-variant' }}>
              {i18n().modules.requirements.components.accordionVideo.testVideo}
            </Typography>
            <Button
              onClick={() => setPlayerVideoProps((oldState) => ({ ...oldState, isVisible: true }))}
              LeadingIcon={<PlayCircleIcon />}
            >
              {i18n().modules.requirements.components.accordionVideo.buttons.executeTest}
            </Button>
          </>
        )}
        {playerVideoPros.isVisible && (
          <Div css={{ display: 'flex', width: '100%', gap: '$md', flexDirection: 'column' }}>
            <video controls muted autoPlay style={{ height: '244px' }}>
              <source src={urlVideo} type="video/mp4" />
            </video>
            <Div
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant="bodyMedium" css={{ color: '$on-surface-variant' }}>
                {i18n().modules.requirements.components.accordionVideo.questionVideo}
              </Typography>
              <Div css={{ display: 'flex', gap: '$md' }}>
                <Button
                  variant="outlined"
                  onClick={() => setPlayerVideoProps({ isVisible: false, testResult: 'fail' })}
                >
                  {i18n().modules.requirements.components.accordionVideo.buttons.no}
                </Button>
                <Button
                  onClick={() => setPlayerVideoProps({ isVisible: false, testResult: 'pass' })}
                >
                  {i18n().modules.requirements.components.accordionVideo.buttons.yes}
                </Button>
              </Div>
            </Div>
          </Div>
        )}
        {!!playerVideoPros.testResult.length && (
          <Div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button
              variant="outlined"
              onClick={() =>
                setPlayerVideoProps({
                  isVisible: true,
                  testResult: ''
                })
              }
            >
              {i18n().modules.requirements.components.accordionVideo.buttons.testAgain}
            </Button>
            {playerVideoPros.testResult === 'pass' ? (
              <CheckCircleIcon fill="$success" />
            ) : (
              <AlertErrorIcon fill="$critical" />
            )}
          </Div>
        )}
      </Div>
    </Accordion>
  )
}
